import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import moment from 'moment';
import momentDurationFormatSetup from "moment-duration-format";
momentDurationFormatSetup(moment);
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { Box, useTheme, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useRootStore } from '../hooks/useRootStore';
import { Skill } from '../data/Skill';
import { MDXEditor, MDXEditorMethods } from '@mdxeditor/editor';
import { MdxPlugins } from './MarkdownEditor/MarkdownEditorPlugins';
import { EmployeeProfile } from '../data/UserProfile';
import ConfirmDialog from './ConfirmDialog';
import useDeviceMedia from '../hooks/useDeviceMedia';

export interface NewActivityData {
    issueId: number,
    skillId: number,
    comment: string,
    timeStart: Date,
    timeEnd: Date,
    isOffDuty: boolean,
}

export default function AddActivityDialog({ startDate, endDate, defaultText, onSubmit, onResume, onTimerCancel }: { startDate: Date, endDate: Date, defaultText?: string, onSubmit: (ActivityData: NewActivityData) => void, onResume: (text: string) => void, onTimerCancel: () => void }) {
    const theme = useTheme();

    const { isMobile, isMobilePortrait } = useDeviceMedia();

    const [timeStart, setTimeStart] = useState(startDate);
    const [timeEnd, setTimeEnd] = useState(endDate);
    const [isOffDuty, setIsOffDuty] = useState(false);
    const [skillId, setSkillId] = useState<number | null>(null);
    const [skills, setSkills] = useState([] as Skill[]);
    const { api, userProfile, setActivityStartTime } = useRootStore();
    const [textNotSpecified, setTextNotSpecified] = useState(false);

    const [error, setError] = useState('');

    const [confirmCancelTimer, setConfirmCancelTimer] = useState(false);

    useEffect(() => {
        if (userProfile) {
            api.getEmployeeSkills(userProfile.ID).then(result => {
                setSkills(result);
                if (result.length == 1) {
                    setSkillId(result[0].ID)
                } else if (result.length > 0) {
                    const lastSkillID = parseInt(localStorage.getItem('lastActivitiSkillID') ?? 'not a number', 10);
                    if (!isNaN(lastSkillID) && result.filter(x => x.ID == lastSkillID)) {
                        setSkillId(lastSkillID);
                    } else {
                        setSkillId(result[0].ID);
                    }
                }
                else {
                    setError("Нет навыков для задач");
                }
            });
        };

    }, [api, userProfile]);

    useEffect(() => {
        setActivityStartTime(timeStart);
    }, [setActivityStartTime, timeStart])
    
    const mdxEditorRef = useRef<MDXEditorMethods>(null);

    const checkActivity = () => {
        const comments = (mdxEditorRef.current?.getMarkdown() ?? '').trim();
        if (comments.trim().length == 0) {
            setTextNotSpecified(true);
            return false;
        }

        return true;
    }

    const postActivity = async () => {
        if (checkActivity()) {
            await onSubmit({
                issueId: 0,
                skillId: skillId ?? 0,
                comment: mdxEditorRef.current?.getMarkdown() ?? '',
                timeStart: moment(new Date()).hours(0).minutes(0).seconds(0).add(timeStart.getHours(), 'hours').add(timeStart.getMinutes(), 'minutes').toDate(),
                timeEnd: moment(new Date()).hours(0).minutes(0).seconds(0).add(timeEnd.getHours(), 'hours').add(timeEnd.getMinutes(), 'minutes').toDate(),
                isOffDuty: isOffDuty,
            });
        }
    }

    const tryCancelTimer = () => {
        setConfirmCancelTimer(true);
    }

    const cancelTimer = () => {        
        setActivityStartTime(null);
        setConfirmCancelTimer(false);
        onTimerCancel();
    }

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Dialog
                maxWidth='lg'
                fullWidth
                open={true}
                onClose={() => onResume((mdxEditorRef.current?.getMarkdown() ?? '').trim())}
                className={"dialog" + (isMobilePortrait ? ' mobile-portrait' : '')}
            >
                {confirmCancelTimer ?
                    <ConfirmDialog
                        text='Выключить таймер без сохранения активности?'
                        cancelBtnText='Нет'
                        confirmBtnText='Да'
                        onCancel={() => setConfirmCancelTimer(false)}
                        onConfirm={() => cancelTimer()}
                    /> : ''}
                <DialogTitle>Добавление активности</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'row' }}>
                        <TimePicker
                            defaultValue={moment(startDate)}
                            onChange={(e) => { if (e) setTimeStart(e.toDate()) }}
                            ampm={false}
                            sx={{ marginRight: 1, flex: 1.5 }}
                            name="timeStart"
                            label="Начало"
                        />
                        <TimePicker
                            defaultValue={moment(endDate)}
                            onChange={(e) => { if (e) setTimeEnd(e.toDate()) }}
                            ampm={false}
                            sx={{ marginRight: 1, flex:1.5 }}
                            name="timeEnd"
                            label="Окончаниe"
                        />
                        {isMobilePortrait ? '' :
                            <TextField sx={{ mr: 1, flex: 1.5 }} label="Часов" disabled /*error={moment.duration(moment(timeEnd).diff(moment(timeStart))).asMinutes() >= 0}*/ variant="outlined" value={moment.duration(moment(timeEnd).diff(moment(timeStart))).format("HH:mm", { trim: false })} />
                        }
                        <FormControl sx={{ flex: 2 }}>
                            <InputLabel id="demo-simple-select-label">Нерабочее время</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                value={isOffDuty ? 1 : 0}
                                label="Нерабочее время"
                                onChange={(e) => setIsOffDuty(e.target.value == 1)}
                            >
                                <MenuItem key={0} value={0}>Нет</MenuItem>
                                <MenuItem key={1} value={1}>Да</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>               
                    <FormControl fullWidth sx={{mt:4}}>
                        <InputLabel id="demo-simple-select-label">Направление *</InputLabel>
                        {skills.length == 5 ? 'нет навыков' :
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Направление"
                                required
                                disabled={skills.length < 2}
                                value={skillId ?? 0}
                                onChange={(e) => setSkillId(typeof e.target.value == 'number' ? e.target.value as number : null)}
                            >
                                {skills.map((w) => <MenuItem key={w.ID} value={w.ID}>{w.Name}</MenuItem>)}
                            </Select>
                        }
                    </FormControl>
                    <Box sx={{ mt: 2, }}>
                        Описание активности:
                    </Box>
                    <Box sx={{ mt: 0.5, textAlign: 'left', minHeight: '300px', border: `1px solid ${textNotSpecified ? 'red' : '#cccccc'}`, borderRadius: 1 }} onClick={() => mdxEditorRef.current?.focus()}>
                        <MDXEditor
                            ref={mdxEditorRef}
                            markdown={defaultText ? defaultText : ''}
                            plugins={MdxPlugins.standardPlugins(api, (userProfile as EmployeeProfile).ActiveIssueId!, '', isMobile)}
                            onChange={() => setTextNotSpecified(false)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between' }}>
                    <Button onClick={tryCancelTimer}>Отменить таймер</Button>
                    <Box>
                        <Button onClick={() => onResume((mdxEditorRef.current?.getMarkdown() ?? '').trim())}>Продолжить работу</Button>
                        <Button disabled={skills.length === 0} onClick={postActivity}>Внести активность</Button>
                    </Box>
                </DialogActions>
                <Box sx={{ color: theme.palette.error.main, textAlign: 'center', pb: 2 }}
                >
                    {error}
                </Box>
            </Dialog>
        </LocalizationProvider>
    );
}